import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  sizes?: string;
  width?: number;
  height?: number;
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  fetchPriority = 'auto',
  sizes = '100vw',
  width,
  height,
  onLoad,
  onError,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Create WebP version of image if it's from our uploads
  const getOptimizedSrc = (originalSrc: string) => {
    if (originalSrc.includes('lovable-uploads')) {
      // For now, we'll use the original since we can't modify Lovable uploads
      // In a real implementation, you'd convert these to WebP
      return originalSrc;
    }
    
    if (originalSrc.includes('unsplash.com')) {
      // Add WebP format parameter for Unsplash
      const url = new URL(originalSrc);
      url.searchParams.set('fm', 'webp');
      return url.toString();
    }
    
    return originalSrc;
  };

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  useEffect(() => {
    // Preload critical images
    if (fetchPriority === 'high' && imgRef.current) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = getOptimizedSrc(src);
      document.head.appendChild(link);
    }
  }, [src, fetchPriority]);

  const optimizedSrc = getOptimizedSrc(src);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <img
        ref={imgRef}
        src={optimizedSrc}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        sizes={sizes}
        width={width}
        height={height}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0',
          hasError && 'hidden'
        )}
        decoding="async"
        style={{
          containIntrinsicSize: width && height ? `${width}px ${height}px` : 'auto'
        }}
      />
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-500">
          Failed to load image
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;